/**
 * Browser → DigitalOcean Spaces upload.
 *
 * Deliberately NOT built on useAppApi()/fetchApi. That axios instance carries
 * `baseURL: getApiRoot()` and `withCredentials: true`, so using it here would rewrite the
 * absolute Spaces URL into an app-relative one, attach this app's httpOnly JWT cookie to a
 * third-party host, and still give no upload progress. All three are disqualifying, so
 * this is a bare XHR instead.
 */

/**
 * PUT a file straight to Spaces against a presigned URL.
 *
 * @param {{ url: string, file: File, contentType: string, onProgress?: (fraction:number)=>void }} params
 * @returns {Promise<{ etag: string|null }>}
 */
export function putToSpaces({ url, file, contentType, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    // NEVER true. This URL points at DigitalOcean, not at this app. Sending credentials
    // would ship the httpOnly JWT cookie to a third party, and it would also turn this
    // into a credentialed CORS request — which a bucket answering with a wildcard origin
    // must reject outright.
    xhr.withCredentials = false;

    // The ONLY header to set. Content-Type is what the presigned URL's signature covers;
    // any additional header either invalidates the signature or provokes a preflight the
    // bucket's CORS rules don't answer. Both surface as an opaque "network error" with
    // nothing useful in the console, so resist adding to this line.
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve({ etag: xhr.getResponseHeader('ETag') });
      else reject(new Error(`Upload failed (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error(
      'Upload failed — network error. If this is the first upload against this bucket, its CORS rules probably do not allow PUT from this origin.',
    ));
    xhr.onabort = () => reject(new Error('Upload cancelled'));

    xhr.send(file);
  });
}

/**
 * Read duration/width/height out of a local file before uploading it.
 *
 * Cosmetic only — these values are the client's word and the server stores them as such
 * (Phase 2's ffmpeg pass replaces them with measured ones). Never resolves to an error:
 * a file whose metadata the browser can't parse still uploads fine, and blocking an upload
 * on a decorative field would be the wrong trade.
 */
export function probeVideo(file) {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    const src = URL.createObjectURL(file);
    let settled = false;
    const done = (out) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(src);
      resolve(out);
    };
    el.preload = 'metadata';
    el.onloadedmetadata = () => done({
      duration_secs: Number.isFinite(el.duration) ? Math.round(el.duration) : null,
      width: el.videoWidth || null,
      height: el.videoHeight || null,
    });
    el.onerror = () => done({});
    // A codec the browser can't parse leaves both handlers unfired forever, which would
    // hang the upload button. Give up after a moment and upload without the metadata.
    setTimeout(() => done({}), 5000);
    el.src = src;
  });
}

/** The `{ error }` body server.js sends for a failure, or the raw axios/XHR message. */
export const errText = (ex) => (ex && ex.response && ex.response.data && ex.response.data.error) || (ex && ex.message) || String(ex);

/**
 * Pull the new row's id out of a create response.
 *
 * korm returns an ARRAY of inserted rows, and server.js object-spreads it on the way out,
 * so what actually arrives here is `{ "0": { id, … } }` — verified against the running
 * app, not guessed. The other shapes are defensive; the same ambiguity silently broke
 * ScrapeJob's enqueue once (see newRowId in models/ScrapeJob.model.js).
 */
export const newRowId = (res) => {
  if (!res || typeof res !== 'object') return null;
  const candidates = [res, res.data, ...(Array.isArray(res) ? res : Object.values(res))];
  for (const c of candidates) {
    if (!c || typeof c !== 'object') continue;
    const row = Array.isArray(c) ? c[0] : c;
    if (row && Number(row.id) > 0) return Number(row.id);
  }
  return null;
};

/**
 * Tell the server an upload died, so the row says why.
 *
 * Best effort by construction. Without this the row sits in `uploading` with a NULL error
 * forever — which is exactly how three videos became undiagnosable orphans. The server
 * refuses to apply this to a row that already reached `ready`, so a late report from a
 * retried upload cannot undo a success.
 */
async function reportFailure(fetchApi, id, ex) {
  if (!id) return;
  try {
    await fetchApi('crud', { model: 'Video', action: 'fail', data: { id, reason: errText(ex) } });
  } catch {
    // Swallowed on purpose: this is diagnostics. Rethrowing here would replace the real
    // upload error with a reporting error and tell the user nothing useful.
  }
}

/**
 * The whole create → presign → PUT → complete handshake, in one place.
 *
 * The bytes go browser → Spaces directly; this app only ever sees the small JSON calls
 * either side of that. Shared between the upload form and the retry action so the two can
 * never drift — the retry exists because a row stuck in `uploading` is genuinely one
 * successful PUT away from being fine.
 *
 * @param {object}   p
 * @param {Function} p.fetchApi    useAppApi()'s fetchApi
 * @param {File}     p.file
 * @param {string}   p.title       ignored when retrying — the row already has one
 * @param {number}   [p.existingId] retry an existing `uploading` row instead of creating one
 * @param {Function} [p.onStage]   progress label
 * @param {Function} [p.onProgress] 0..1 upload fraction
 * @returns {Promise<number>} the video row id
 */
export async function runUpload({ fetchApi, file, title, existingId = null, onStage, onProgress }) {
  const stage = (s) => { if (onStage) onStage(s); };
  let id = existingId;

  stage('Preparing…');
  const meta = await probeVideo(file);

  // 1. Create the row. The server mints the public_id and storage key here; nothing about
  //    where the bytes land is decided by this client. Skipped on a retry.
  if (!id) {
    const created = await fetchApi('crud', {
      model: 'Video',
      action: 'create',
      data: { title: String(title || '').trim(), mime: file.type, size: file.size, ...meta },
    });
    id = newRowId(created);
    if (!id) throw new Error('The server did not return a video id');
  }

  try {
    // 2. Get a presigned PUT for that row. The server refuses unless it is still
    //    `uploading`, so a retry can never overwrite a finished video.
    stage('Uploading…');
    const signed = await fetchApi('videoUploadUrl', { id });

    // 3. Send the file straight to Spaces, then tell the server to verify it landed.
    await putToSpaces({ url: signed.url, file, contentType: signed.contentType, onProgress });

    stage('Finishing…');
    await fetchApi('crud', { model: 'Video', action: 'complete', data: { id } });
  } catch (ex) {
    await reportFailure(fetchApi, id, ex);
    throw ex;
  }

  return id;
}
